import { Button } from '@components'
import { updateFile } from '@query'
import { QueryClient } from '@tanstack/react-query'
import React, { useEffect, useReducer, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { UPLOAD_NOTE_TOAST_DURATION_MS, uploadNoteToastReducer } from '@utils/attachmentNotes'
import { ToastFilePreview, ToastImagePreview } from './ToastFilePreview'

type UploadPhase = 'uploading' | 'scanning' | 'done' | 'infected' | 'failed'

export interface UploadToastProps {
  file: File
  toastId: string
  slug: string
  canEditNotes: boolean
  queryClient: QueryClient
  phase: UploadPhase
  fileId?: number
  progress?: number
  onCancel?: () => void
}

export const UploadToast: React.FC<UploadToastProps> = ({
  file,
  toastId,
  slug,
  canEditNotes,
  queryClient,
  phase,
  fileId,
  progress,
  onCancel
}) => {
  const [t] = useTranslation()
  const [note, setNote] = useState('')
  const [saveQueued, setSaveQueued] = useState(false)
  const [state, dispatch] = useReducer(uploadNoteToastReducer, 'collapsed')
  const notesEnabled =
    canEditNotes &&
    (phase === 'uploading' || phase === 'scanning' || (phase === 'done' && fileId != null))
  const showNoteEditor = notesEnabled && (state === 'expanded' || state === 'saving')

  const persistNote = async (id: number) => {
    dispatch({ type: 'SAVE' })

    try {
      await updateFile({ slug, fileId: id }, { notes: note.trim() || null })
      await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
      if (phase === 'done') {
        toast.dismiss(toastId)
      } else {
        dispatch({ type: 'CANCEL' })
      }
    } catch {
      dispatch({ type: 'SAVE_FAILED' })
      toast.error(t('general.error'))
    }
  }

  useEffect(() => {
    if (fileId == null || !saveQueued) return
    setSaveQueued(false)
    void persistNote(fileId)
  }, [fileId, saveQueued, persistNote])

  useEffect(() => {
    if (phase !== 'done' || !notesEnabled || state !== 'collapsed') return

    const timer = window.setTimeout(() => {
      toast.dismiss(toastId)
    }, UPLOAD_NOTE_TOAST_DURATION_MS)

    return () => window.clearTimeout(timer)
  }, [notesEnabled, phase, state, toastId])

  const cancelNote = () => {
    setSaveQueued(false)
    dispatch({ type: 'CANCEL' })
  }

  const handleSave = () => {
    if (fileId == null) {
      setSaveQueued(true)
    } else {
      void persistNote(fileId)
    }
  }

  return (
    <ToastContent>
      <ToastMain>
        <ToastFilePreview
          file={file}
          progress={progress}
          onCancel={onCancel}
          detail={
            phase === 'scanning'
              ? t('files.malwareScan.pending')
              : phase === 'infected'
              ? t('files.malwareScan.infected')
              : undefined
          }
        />

        {notesEnabled && state === 'collapsed' && (
          <AddNoteButton type="button" onClick={() => dispatch({ type: 'ADD_NOTE' })}>
            {t('files.notes.add')}
          </AddNoteButton>
        )}

        {showNoteEditor && (
          <NoteEditor>
            <NoteDescription>{t('files.notes.description')}</NoteDescription>
            <NoteTextarea
              value={note}
              onChange={event => setNote(event.target.value)}
              placeholder={t('files.notes.placeholder')}
              rows={3}
              disabled={state === 'saving'}
            />
            {saveQueued && (
              <NoteDescription>{t('files.notes.savedWhenUploadFinishes')}</NoteDescription>
            )}
            <NoteActions>
              <CancelNoteButton
                type="button"
                size="sm"
                intent="default"
                isSecondary
                disabled={state === 'saving'}
                onClick={cancelNote}
              >
                {t('general.cancel')}
              </CancelNoteButton>
              <Button
                type="button"
                size="sm"
                intent="success"
                showSpinner={state === 'saving'}
                disabled={state === 'saving'}
                onClick={handleSave}
              >
                {t('general.save')}
              </Button>
            </NoteActions>
          </NoteEditor>
        )}
      </ToastMain>
      <ToastImagePreview file={file} />
    </ToastContent>
  )
}

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  width: 280px;
  max-width: 100%;
  min-width: 0;
`

const ToastMain = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const AddNoteButton = styled.button`
  align-self: flex-start;
  border: none;
  background: transparent;
  color: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  white-space: normal;

  &:hover {
    opacity: 0.85;
  }
`

const CancelNoteButton = styled(Button)`
  &&& {
    background: transparent;
    color: ${({ theme }) => theme.colors.neutralWhite};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.neutralWhite}66;
  }
`

const NoteEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  max-width: 100%;
  min-width: 0;
`

const NoteDescription = styled.p`
  margin: 0;
  max-width: 100%;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: inherit;
  opacity: 0.85;
  overflow-wrap: anywhere;
  white-space: normal;
`

const NoteTextarea = styled.textarea`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  resize: vertical;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.xs}rem;
  font: inherit;
  color: ${({ theme }) => theme.colors.menuBg};
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const NoteActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
